import React from 'react';
import { Gauge, Minus, Plus } from 'lucide-react';
import { Button } from './ui/button';

/**
 * ReadingSpeedDial — distinct, gear-shifter style speed selector for Flash + Chunk
 * reading modes. Visually different from the audio TTS slider so users feel the
 * shift between "audio playback" (analog slider) and "reading drill" (discrete gears).
 *
 * Props
 *  - value, onChange, min, max, step
 *  - presets: [{ label, value }]   (used as the gear stops)
 *  - format: (v) => string
 *  - accentClass / accentBorder / accentText
 *  - testIdPrefix
 */
const ReadingSpeedDial = ({
  value,
  onChange,
  min,
  max,
  step,
  presets = [],
  format = (v) => `${v}`,
  accentClass = 'bg-indigo-600',
  accentBorderClass = 'border-indigo-500',
  accentTextClass = 'text-indigo-300',
  testIdPrefix = 'speed',
}) => {
  const clamp = (n) => Math.min(max, Math.max(min, n));
  const nudge = (delta) => onChange(clamp(value + delta));
  const activePreset = presets.find((p) => p.value === value)?.label;
  // For visual gear position even when value isn't on a preset:
  // pick the closest preset and treat it as "near" so the indicator slides.
  const nearestIdx = presets.reduce(
    (best, p, i) => (Math.abs(p.value - value) < Math.abs(presets[best].value - value) ? i : best),
    0,
  );

  return (
    <div
      className="rounded-xl border border-slate-700/70 bg-gradient-to-br from-slate-900/60 to-slate-800/40 p-3 sm:p-4 space-y-3"
      data-testid={`${testIdPrefix}-speed-dial`}
    >
      {/* Header: gear icon + current numeric */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Gauge className={`w-4 h-4 shrink-0 ${accentTextClass}`} />
          <span className="text-xs uppercase tracking-widest font-semibold text-gray-400">
            Reading Gear
          </span>
        </div>
        <span
          className={`text-base sm:text-lg font-bold ${accentTextClass} tabular-nums`}
          data-testid={`${testIdPrefix}-speed-value`}
        >
          {format(value)}
        </span>
      </div>

      {/* Segmented gear selector with sliding indicator */}
      {presets.length > 0 && (
        <div
          className="relative grid gap-1 rounded-lg border border-slate-700 bg-slate-950/50 p-1"
          style={{ gridTemplateColumns: `repeat(${presets.length}, minmax(0, 1fr))` }}
          data-testid={`${testIdPrefix}-speed-presets`}
        >
          {/* Sliding active indicator */}
          <span
            className={`absolute top-1 bottom-1 rounded-md ${accentClass} shadow-lg transition-all duration-300 ease-out`}
            style={{
              left: `calc(${(nearestIdx / presets.length) * 100}% + 4px)`,
              width: `calc(${100 / presets.length}% - 8px)`,
              opacity: activePreset ? 1 : 0.45,
            }}
            aria-hidden="true"
          />
          {presets.map((p) => {
            const active = activePreset === p.label;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => onChange(p.value)}
                data-testid={`${testIdPrefix}-speed-preset-${p.label.toLowerCase()}`}
                className={`relative z-10 px-2 py-2.5 text-xs sm:text-sm font-bold uppercase tracking-wider rounded-md transition-colors ${
                  active ? 'text-white' : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Fine-tune row: tiny ± buttons with a "fine tune" caption */}
      <div className="flex items-center justify-center gap-3 pt-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => nudge(-step)}
          disabled={value <= min}
          className={`h-8 px-3 border-slate-600 bg-slate-900/40 hover:${accentClass} hover:text-white text-xs`}
          data-testid={`${testIdPrefix}-speed-decrease`}
          aria-label="Slower (fine tune)"
        >
          <Minus className="w-3.5 h-3.5 mr-1" /> Slower
        </Button>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
          fine tune
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => nudge(step)}
          disabled={value >= max}
          className={`h-8 px-3 border-slate-600 bg-slate-900/40 hover:${accentClass} hover:text-white text-xs`}
          data-testid={`${testIdPrefix}-speed-increase`}
          aria-label="Faster (fine tune)"
        >
          Faster <Plus className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default ReadingSpeedDial;
